import {
  deviceReportFingerprint,
  type DevicePermissionReport,
  type DeviceSetupReport,
} from '@shared/setup'

/**
 * The device's latest setup report and when it was last sent. The device
 * registry attaches `permissions` + `setup` to a register / heartbeat only when
 * the report changed or the last send is a day old; `requestDeviceReportNow()`
 * asks for one heartbeat right away (after a setup step changed or setup
 * finished).
 */
export type DeviceReport = { permissions: DevicePermissionReport; setup: DeviceSetupReport }

/** Re-send an unchanged report at least this often (keeps the admin view fresh). */
export const DEVICE_REPORT_RESEND_MS = 24 * 60 * 60 * 1000

const SENT_KEY_PREFIX = 'ascend-setup-report-sent:'
const NOW_EVENT = 'ascend-setup-report-now'

let latest: { userId: string; report: DeviceReport } | null = null

export function setLatestDeviceReport(userId: string, report: DeviceReport): void {
  latest = { userId, report }
}

export function clearLatestDeviceReport(): void {
  latest = null
}

type Sent = { fingerprint: string; at: number }

function readSent(userId: string): Sent | null {
  try {
    const raw = localStorage.getItem(SENT_KEY_PREFIX + userId)
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<Sent>
    return typeof v.fingerprint === 'string' && typeof v.at === 'number' ? (v as Sent) : null
  } catch {
    return null
  }
}

/**
 * The AWSJSON report fields to attach to this sync, or null when nothing
 * needs sending. Call `markDeviceReportSent` once the sync succeeded.
 */
export function deviceReportForSync(
  userId: string,
  nowMs: number,
  opts: { force?: boolean } = {},
): { permissions: string; setup: string; fingerprint: string } | null {
  if (!latest || latest.userId !== userId) return null
  const fingerprint = deviceReportFingerprint(latest.report.permissions, latest.report.setup)
  const sent = readSent(userId)
  const due = !sent || sent.fingerprint !== fingerprint || nowMs - sent.at >= DEVICE_REPORT_RESEND_MS
  if (!due && !opts.force) return null
  return {
    permissions: JSON.stringify(latest.report.permissions),
    setup: JSON.stringify(latest.report.setup),
    fingerprint,
  }
}

export function markDeviceReportSent(userId: string, fingerprint: string, nowMs: number): void {
  try {
    localStorage.setItem(SENT_KEY_PREFIX + userId, JSON.stringify({ fingerprint, at: nowMs }))
  } catch {
    /* storage unavailable: the next sync simply sends again */
  }
}

/** Ask the device registry for one heartbeat now (bypasses its 60 s throttle). */
export function requestDeviceReportNow(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(NOW_EVENT))
}

export function onDeviceReportNow(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(NOW_EVENT, listener)
  return () => window.removeEventListener(NOW_EVENT, listener)
}

const PROBE_EVENT = 'ascend-setup-probe-refresh'

/**
 * Ask the setup host to re-run the device permission probes now (a permission
 * changed outside setup — e.g. the Device permissions page, or a return from
 * the OS Settings). A changed probe result re-sends the device report, so the
 * admin view follows within seconds.
 */
export function requestSetupProbeRefresh(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(PROBE_EVENT))
}

export function onSetupProbeRefresh(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(PROBE_EVENT, listener)
  return () => window.removeEventListener(PROBE_EVENT, listener)
}
